import { useMemo, type ReactNode } from "react";

import {
  ColocaDataTable,
  type ColocaDataTableColumn,
} from "@/shared/components/data-display/ColocaDataTable";
import { useTranslations } from "@/shared/lang/useTranslations";

import type { ApiKeyRow } from "../hooks/useApiKeysCatalog";
import { useLastUsedLabel } from "../utils/lastUsed";
import { ApiKeyAccessCell } from "./ApiKeyAccessCell";
import { ApiKeyIdentityCell } from "./ApiKeyIdentityCell";
import { ApiKeyOrganizationCell } from "./ApiKeyOrganizationCell";
import { ApiKeyRowActions } from "./ApiKeyRowActions";
import type { ApiKeysCardsProps } from "./ApiKeysCards";
import { ApiKeyStatusPill } from "./ApiKeyStatusPill";

/** The card props plus the two the table alone renders — see `ApiKeysCardsProps` for why it extends it. */
export interface ApiKeysTableProps extends ApiKeysCardsProps {
  empty: ReactNode;
  footer?: ReactNode;
}

/**
 * The key catalog at `lg` and up; below that the same rows render as cards.
 *
 * No sorting — `GET /api-keys` fixes the order at `created_at DESC` and accepts no `sort`. And no row click:
 * unlike roles and policies, a key has no single "open" action, so every affordance is an explicit control
 * and the row itself is inert.
 */
export function ApiKeysTable({
  rows,
  loading,
  empty,
  footer,
  catalogUnavailable,
  canUpdate,
  canManageRoles,
  showOrganization,
  lookupOrganization,
  isDirectoryPending,
  isOrganizationPending,
  onManageRoles,
  onEditIps,
  onDisable,
  onEnable,
}: ApiKeysTableProps) {
  const { t } = useTranslations("settings");
  const lastUsedLabel = useLastUsedLabel();

  const columns = useMemo<ColocaDataTableColumn<ApiKeyRow>[]>(
    () => [
      {
        id: "key",
        header: t("access_col_key"),
        headerClassName: "min-w-[280px]",
        cell: (row) => (
          <ApiKeyIdentityCell apiKey={row.key} lastUsedLabel={lastUsedLabel(row.key.last_used_at)} />
        ),
      },
      ...(showOrganization
        ? [
            {
              id: "organization",
              header: t("access_apikeys_col_organization"),
              headerClassName: "w-[200px]",
              cell: (row: ApiKeyRow) => (
                <ApiKeyOrganizationCell
                  organizationId={row.key.organization_id}
                  entry={lookupOrganization(row.key.organization_id)}
                  isDirectoryPending={isDirectoryPending}
                  isIdPending={isOrganizationPending(row.key.organization_id)}
                />
              ),
            },
          ]
        : []),
      {
        id: "access",
        header: t("access_col_roles"),
        headerClassName: "w-[210px]",
        cell: (row) => (
          <ApiKeyAccessCell
            row={row}
            catalogUnavailable={catalogUnavailable}
            // Gated on `rolesFailed` too: opening the drawer for a key whose roles are UNKNOWN would render
            // "0 assigned" with every box clear — indistinguishable from a confirmed "holds nothing".
            onOpen={catalogUnavailable || row.rolesFailed ? undefined : () => onManageRoles(row)}
          />
        ),
      },
      {
        id: "status",
        header: t("access_apikeys_col_status"),
        headerClassName: "w-[130px]",
        cell: (row) => <ApiKeyStatusPill status={row.status} />,
      },
      {
        id: "actions",
        // Visually hidden rather than empty: a `th` with no content leaves the column unnamed for a screen
        // reader.
        header: <span className="sr-only">{t("access_page_col_actions")}</span>,
        headerClassName: "w-[120px]",
        cellClassName: "text-right",
        cell: (row) => (
          <ApiKeyRowActions
            row={row}
            canOpenRoles={!catalogUnavailable && !row.rolesFailed}
            canUpdate={canUpdate}
            canManageRoles={canManageRoles}
            onManageRoles={onManageRoles}
            onEditIps={onEditIps}
            onDisable={onDisable}
            onEnable={onEnable}
          />
        ),
      },
    ],
    [
      t,
      lastUsedLabel,
      showOrganization,
      lookupOrganization,
      isDirectoryPending,
      isOrganizationPending,
      catalogUnavailable,
      canManageRoles,
      canUpdate,
      onManageRoles,
      onEditIps,
      onDisable,
      onEnable,
    ],
  );

  return (
    <ColocaDataTable<ApiKeyRow>
      columns={columns}
      rows={rows as ApiKeyRow[]}
      rowKey={(row) => row.key.id}
      loading={loading}
      skeletonRows={8}
      empty={empty}
      footer={footer}
      // The page already wraps this in a `ColocaSurface`; a second frame would double the hairline and notch
      // every corner. Sum of the declared column widths — below it the table scrolls.
      frame="none"
      minWidth={940}
      // Dim, never tint. An expired key is dimmed exactly like a disabled one because both are equally dead
      // at the gate; which of the two it is, the status pill says.
      rowClassName={(row) => (row.status === "active" ? undefined : "opacity-60")}
    />
  );
}
